import { Component, ElementRef, Injector, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import {
  FcFacilityPortalPersonListItemDto,
  FcModifyFacilityPortalPersonListItemDto,
  FcModifyLanguageListItemDto,
  PersonFormGroupDTO
} from 'webcommon/shared';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-facility-portal-person-list-modal',
  templateUrl: './fc-facility-portal-person-list-modal.component.html',
  styleUrls: ['./fc-facility-portal-person-list-modal.component.less']
})
export class FcFacilityPortalPersonListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  @ViewChildren('phoneNumber') phoneNumberInputs: QueryList<ElementRef<HTMLInputElement>>;
  public override baseId = 'fc-facility-portal-person-list-modal';

  // List Item with default value
  public listItem: FcFacilityPortalPersonListItemDto = {
    Id: '',
    PersonUid: '',
    PatientCenterUid: '',
    PatientCenter: '',
    Inactive: false,
    Notes: '',

    // Name details
    Prefix: '',
    FirstName: '',
    MiddleName: '',
    LastName: '',
    Suffix: '',
    MaidenName: '',
    PronounUid: '',
    AkaFirstName: '',
    AkaLastName: '',

    // Other information
    GenderUid: '',
    BirthGenderUid: '',
    GenderIdentityUid: '',
    SexualOrientationUid: '',
    BirthDate: null,
    BirthTime: null,
    DeathDate: '',
    MaritalStatusUid: '',
    RaceUids: [],
    EthnicityUids: [],
    LanguageUid: '',
    TranslatorRequired: false,
    DominantHandUid: '',
    SocialSecurityNumber: '',
    DriverLicenseNumber: '',
    DriverLicenseState: '',

    // Addresses
    BadAddress1: false,
    Address1: {
      ZipUid: '',
      CountyName: '',
      CountyCode: '',
      Address1: '',
      Address2: '',
      City: '',
      State: '',
      Zip: '',
      Country: '',
    },
    Address2: {
      ZipUid: '',
      CountyName: '',
      CountyCode: '',
      Address1: '',
      Address2: '',
      City: '',
      State: '',
      Zip: '',
      Country: '',
    },

    // Contact information
    Phone1: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: ''
    },
    Phone1MessagingAllowed: false,
    Phone1SmsEnabledDefault: false,
    Phone2: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: ''
    },
    Phone2MessagingAllowed: false,
    Phone2SmsEnabledDefault: false,
    Phone3: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: ''
    },
    Phone3MessagingAllowed: false,
    Phone3SmsEnabledDefault: false,
    Phone4: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: ''
    },
    Phone4MessagingAllowed: false,
    Phone4SmsEnabledDefault: false,
    Email1: '',
    Email2: '',
    Email1MessagingAllowed: false,
    Email2MessagingAllowed: false,
    NoEmailAddressProvided: false,
    PreferredContactMethod: '',
  };


  // Form Group
  public form: FormGroup = new FormGroup({});

  constructor(injector: Injector) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();

    // Load List Item and set default validation rules
    this.loadListItem({
      // Name details
      Prefix: {
        MaxLength: 25,
      },
      FirstName: {
        IsRequired: true,
        MaxLength: 50,
      },
      MiddleName: {
        MaxLength: 50,
      },
      LastName: {
        IsRequired: true,
        MaxLength: 50,
      },
      Suffix: {
        MaxLength: 50,
      },
      MaidenName: {
        MaxLength: 50,
      },
      AkaFirstName: {
        MaxLength: 50,
      },
      AkaLastName: {
        MaxLength: 50,
      },
      SocialSecurityNumber: {
        MaxLength: 11,
        RegExp: '([0-9]{3}-[0-9]{2}-[0-9]{4})',
      },
      DriverLicenseNumber: {
        MaxLength: 25,
      },
      DriverLicenseState: {
        MaxLength: 3,
      },
      PhoneNumber: {
        MaxLength: 22,
        RegExp: '^(([0-9]{3}-[0-9]{4})|((\\([0-9]{3}\\)[0-9]{3}-[0-9]{4})( x[0-9]{1,7})?))$',
      },
      PhoneType: {},
      Notes: {
        MaxLength: 65535,
      },
    });
  }

  public setForm(): void {
    this.validateForm();
  }

  public override save(): void {
    const values: PersonFormGroupDTO = this.form.getRawValue();

    const emailOne = values?.emails?.[0] ?? null;
    const emailTwo = values?.emails?.[1] ?? null;
    const otherInformation = values.otherInformation;
    const nameDetails = values.nameDetails;

    const Address1 = {
      Address1: values?.primaryAddress?.addressOne ?? '',
      Address2: values?.primaryAddress?.addressTwo ?? '',
      City: values?.primaryAddress?.city ?? '',
      State: values?.primaryAddress?.state ?? '',
      Zip: values?.primaryAddress?.zip ?? '',
      Country: values?.primaryAddress?.country ?? '',
      CountyName: values?.primaryAddress?.countyName ?? '',
      CountyCode: values?.primaryAddress?.countyCode ?? '',
    };
    const Address2 = {
      Address1: values?.secondaryAddress?.addressOne ?? '',
      Address2: values?.secondaryAddress?.addressTwo ?? '',
      City: values?.secondaryAddress?.city ?? '',
      State: values?.secondaryAddress?.state ?? '',
      Zip: values?.secondaryAddress?.zip ?? '',
      Country: values?.secondaryAddress?.country ?? '',
      CountyName: values?.secondaryAddress?.countyName ?? '',
      CountyCode: values?.secondaryAddress?.countyCode ?? '',
    };

    const modifyItemDto: FcModifyFacilityPortalPersonListItemDto = {
      // Name Details
      Prefix: nameDetails.title,
      FirstName: nameDetails.firstName,
      MiddleName: nameDetails.middleName,
      LastName: nameDetails.lastName,
      Suffix: nameDetails.suffix,
      MaidenName: nameDetails.maidenName,
      PronounUid: this.parseFindControlValue(nameDetails.pronounUid),
      AkaFirstName: nameDetails.akaFirstName,
      AkaLastName: nameDetails.akaLastName,

      // Other Information
      GenderUid: this.parseFindControlValue(otherInformation.genderUid),
      BirthGenderUid: this.parseFindControlValue(otherInformation.birthGenderUid),
      GenderIdentityUid: this.parseFindControlValue(otherInformation.genderIdentityUid),
      SexualOrientationUid: this.parseFindControlValue(otherInformation.sexualOrientationUid),
      BirthDate: otherInformation.birthDate?.toISOString() ?? '',
      DeathDate: otherInformation.deathDate?.toISOString() ?? '',
      MaritalStatusUid: this.parseFindControlValue(otherInformation.maritalStatusUid),
      RaceUids: this.parseFindControlValue(otherInformation.raceUids, true),
      EthnicityUids: this.parseFindControlValue(otherInformation.ethnicityUids, true),
      LanguageUid: this.parseFindControlValue(otherInformation.preferredLanguageUid),
      TranslatorRequired: otherInformation.translatorRequired,
      DominantHandUid: this.parseFindControlValue(otherInformation.dominantHandUid),
      SocialSecurityNumber: otherInformation.socialSecurityNumber,
      DriverLicenseNumber: otherInformation.driversLicenseNumber,
      DriverLicenseState: otherInformation.driversLicenseState,

      BadAddress1: values.undeliverableToPrimaryAddress,
      Address1,
      Address2,

      Email1: emailOne?.email ?? '',
      Email1MessagingAllowed: emailOne?.marketing ?? false,
      Email2: emailTwo?.email ?? '',
      Email2MessagingAllowed: emailTwo?.marketing ?? false,
      NoEmailAddressProvided: values.declinedOrHasNoEmailAddress ?? false,
      PreferredContactMethod: values.preferredContactMethod ?? '',

      Notes: values.notes,
    };

    const phoneOne = values?.phones?.[0] ?? null;
    const phoneTwo = values?.phones?.[1] ?? null;
    const phoneThree = values?.phones?.[2] ?? null;
    const phoneFour = values?.phones?.[3] ?? null;

    if (phoneOne?.phoneNumber && phoneOne?.phoneType && phoneOne?.phoneTypeUid) {
      modifyItemDto.Phone1 = {
        PhoneNumber: phoneOne.phoneNumber,
        PhoneType: phoneOne.phoneType,
        PhoneTypeUid: this.parseFindControlValue(phoneOne.phoneTypeUid),
      };
    }
    modifyItemDto.Phone1MessagingAllowed = phoneOne?.marketing ?? false;

    if (phoneTwo?.phoneNumber && phoneTwo?.phoneType && phoneTwo?.phoneTypeUid) {
      modifyItemDto.Phone2 = {
        PhoneNumber: phoneTwo.phoneNumber,
        PhoneType: phoneTwo.phoneType,
        PhoneTypeUid: this.parseFindControlValue(phoneTwo.phoneTypeUid),
      };
    }
    modifyItemDto.Phone2MessagingAllowed = phoneTwo?.marketing ?? false;

    if (phoneThree?.phoneNumber && phoneThree?.phoneType && phoneThree?.phoneTypeUid) {
      modifyItemDto.Phone3 = {
        PhoneNumber: phoneThree.phoneNumber,
        PhoneType: phoneThree.phoneType,
        PhoneTypeUid: this.parseFindControlValue(phoneThree.phoneTypeUid),
      };
    }
    modifyItemDto.Phone3MessagingAllowed = phoneThree?.marketing ?? false;

    if (phoneFour?.phoneNumber && phoneFour?.phoneType && phoneFour?.phoneTypeUid) {
      modifyItemDto.Phone4 = {
        PhoneNumber: phoneFour.phoneNumber,
        PhoneType: phoneFour.phoneType,
        PhoneTypeUid: this.parseFindControlValue(phoneFour.phoneTypeUid),
      };
    }
    modifyItemDto.Phone4MessagingAllowed = phoneFour?.marketing ?? false;

    super.save<FcModifyLanguageListItemDto>(modifyItemDto);
  }
}
